/**
 * MMOUnit - MMO mode unit entity
 * Displays units in the persistent MMO world with server-driven state
 */

import Phaser from 'phaser';
import { axialToPixel, type HexCoord } from '../../shared/HexUtils';
import type { MMOUnit as MMOUnitData } from '../../managers/MMOStateManager';
import { EventBus } from '../../utils/EventBus';

// Player colors (index by hash of UUID for consistent colors)
const PLAYER_COLORS = [
  0x3388ee, // Blue
  0xee4444, // Red
  0x44cc55, // Green
  0xeeee44, // Yellow
  0xf08822, // Orange
  0xbb55ee, // Purple
  0x00ffff, // Cyan
  0xff00ff, // Magenta
];

export class MMOUnit extends Phaser.GameObjects.Container {
  // Identity
  public id: string;
  public unitType: string;
  public shipVariant: string | null;
  public ownerId: string;
  public hexCoord: HexCoord;

  // Stats
  public currentHp: number;
  public maxHp: number;
  public currentShields: number;
  public maxShields: number;
  public speed: number;
  public attackRange: number;
  public attack: number;
  public defense: number;

  // MMO-specific
  public movementOrder: { q: number; r: number; turnsRemaining: number } | null = null;
  public dockedAtPlanetId: string | null = null;
  public cargoCapacity: number = 100;
  public cargoUsed: number = 0;
  public miningAsteroidId: string | null = null;
  public miningProgress: number = 0;
  public maxMineralRarity: string = 'common';
  public laserTier: string = 'mk0';
  public yieldBonus: number = 0;
  public laserConditionPercent: number = 100;
  public customName: string | null = null;
  public ownerName: string | null = null;

  public activeEffects: Array<{ effectType: string; remainingTicks: number }> = [];
  public abilities: Array<{
    abilityTypeId: number;
    name: string;
    effectType: string;
    cooldownTicks: number;
    durationTicks: number;
    currentCooldown: number;
    description: string;
  }> = [];

  // Visual elements
  private sprite: Phaser.GameObjects.Graphics;
  private shipSprite: Phaser.GameObjects.Image | null = null;
  private healthBar: Phaser.GameObjects.Graphics;
  private shieldBar: Phaser.GameObjects.Graphics;
  private ownerIndicator: Phaser.GameObjects.Graphics;
  private movementIndicator: Phaser.GameObjects.Graphics | null = null;
  private movementText: Phaser.GameObjects.Text | null = null;
  private nameLabel: Phaser.GameObjects.Text | null = null;
  private effectDots: Phaser.GameObjects.Graphics | null = null;
  private engagementRing: Phaser.GameObjects.Graphics | null = null;
  private engagementTween: Phaser.Tweens.Tween | null = null;
  private _isEngaged: boolean = false;

  // Selection glow state
  private _isSelected: boolean = false;
  private selectionGlow: Phaser.GameObjects.Graphics | null = null;
  private selectionGlowTween: Phaser.Tweens.Tween | null = null;
  private selectionScaleTween: Phaser.Tweens.Tween | null = null;
  private selectionGlowColor: number = 0x44ff44;
  private bountyPulseTween: Phaser.Tweens.Tween | null = null;
  private baseScale: number = 1;

  // Docked state (hidden from map)
  private _isDocked: boolean = false;
  public get isDocked(): boolean { return this._isDocked; }

  // Movement smoothing
  private moveTween: Phaser.Tweens.Tween | null = null;
  private isFirstPosition: boolean = true;

  // Status effect animations
  private evasiveTween: Phaser.Tweens.Tween | null = null;
  private shieldBoostRing: Phaser.GameObjects.Graphics | null = null;
  private shieldBoostTween: Phaser.Tweens.Tween | null = null;
  private sensorBoostRing: Phaser.GameObjects.Graphics | null = null;
  private previousEffectTypes: Set<string> = new Set();
  private lastMoveDirection: { dx: number; dy: number } = { dx: 0, dy: 0 };

  constructor(scene: Phaser.Scene, data: MMOUnitData) {
    const pixel = axialToPixel(data.position.q, data.position.r);
    super(scene, pixel.x, pixel.y);

    // Initialize from server data
    this.id = data.id;
    this.unitType = data.type;
    this.shipVariant = (data as any).shipVariant || null;
    this.ownerId = data.ownerId;
    this.hexCoord = data.position;
    this.currentHp = data.currentHp;
    this.maxHp = data.maxHp;
    this.currentShields = data.shields;
    this.maxShields = data.maxShields;
    this.speed = data.speed;
    this.attackRange = data.attackRange;
    this.attack = data.attack;
    this.defense = data.defense;
    this.movementOrder = data.movementOrder;
    this.cargoCapacity = data.cargoCapacity ?? 100;
    this.cargoUsed = data.cargoUsed ?? 0;
    this.miningAsteroidId = data.miningAsteroidId ?? null;
    this.miningProgress = data.miningProgress ?? 0;
    this.activeEffects = data.activeEffects || [];
    this.abilities = data.abilities || [];
    this.customName = (data as any).customName || null;
    this.ownerName = (data as any).ownerName || null;

    // Create visual components
    this.ownerIndicator = this.scene.add.graphics();
    this.add(this.ownerIndicator);

    this.sprite = this.scene.add.graphics();
    this.add(this.sprite);

    this.healthBar = this.scene.add.graphics();
    this.add(this.healthBar);

    this.shieldBar = this.scene.add.graphics();
    this.add(this.shieldBar);

    // Draw visuals
    this.drawVisuals();

    // Set depth (units above planets and asteroids)
    this.setDepth(10);

    // Make interactive - hitbox centered on visual (triangle from y=-20 to y=15, center at y=-2.5)
    // Using 60x60 on desktop, expanded to 80x80 on touch devices to meet 48px minimum tap target
    const _isCoarse = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(pointer: coarse)').matches;
    const hitW = _isCoarse ? 80 : 60;
    const hitH = _isCoarse ? 80 : 60;
    this.setSize(hitW, hitH);
    this.setInteractive(
      new Phaser.Geom.Rectangle(-hitW / 2, -hitH / 2 - 3, hitW, hitH),
      Phaser.Geom.Rectangle.Contains,
    );

    // NOTE: No sprite-level 'pointerdown' handler. Phaser's per-sprite pointerdown
    // fires the instant a finger/cursor touches the sprite — which fires BEFORE
    // the scene's drag-threshold check and causes touches on a ship to select it
    // immediately, aborting any intended camera pan. Selection is handled in
    // MMOGameScene.onPointerUp via getEntityAtWorldPoint(), which runs only if
    // the pointer never exceeded the drag-vs-tap threshold. setInteractive() is
    // still required because getEntityAtWorldPoint() reads entity.input.hitArea.

    // Hide immediately if loaded in docked state
    if (data.dockedAtPlanetId) {
      this.dockedAtPlanetId = data.dockedAtPlanetId;
      this.enterDockedState();
    }

    // Note: Do NOT call scene.add.existing(this) here!
    // The entity will be added to the entitiesContainer in MMOGameScene.renderUnit()
    // Adding to both scene and container causes the Input Manager to lose track of the object
  }

  /**
   * Update from server state
   */
  public updateFromServer(data: MMOUnitData): void {
    // Skip if scene has been destroyed (but allow updates when paused)
    if (!this.scene || !this.scene.sys) {
      return;
    }

    // Update stats
    this.currentHp = data.currentHp;
    this.maxHp = data.maxHp;
    this.currentShields = data.shields;
    this.maxShields = data.maxShields;
    this.speed = data.speed;
    this.attackRange = data.attackRange;
    this.attack = data.attack;
    this.defense = data.defense;
    this.movementOrder = data.movementOrder;
    this.cargoCapacity = data.cargoCapacity ?? this.cargoCapacity;
    this.cargoUsed = data.cargoUsed ?? this.cargoUsed;
    this.miningAsteroidId = data.miningAsteroidId !== undefined ? data.miningAsteroidId : this.miningAsteroidId;
    this.miningProgress = data.miningProgress !== undefined ? data.miningProgress : this.miningProgress;
    this.activeEffects = data.activeEffects || [];
    this.abilities = data.abilities || [];
    if ((data as any).customName !== undefined) this.customName = (data as any).customName || null;
    if ((data as any).ownerName !== undefined) this.ownerName = (data as any).ownerName || null;

    // Tick data is authoritative for docked state
    const serverDocked = data.dockedAtPlanetId || null;
    if (serverDocked) {
      this.dockedAtPlanetId = serverDocked;
      if (!this._isDocked) this.enterDockedState();
    } else if (this.dockedAtPlanetId || this._isDocked) {
      this.dockedAtPlanetId = null;
      if (this._isDocked) this.exitDockedState();
    }

    // Skip all visual updates for docked units (they're invisible)
    if (this._isDocked) return;

    // Update position if changed
    if (data.position.q !== this.hexCoord.q || data.position.r !== this.hexCoord.r) {
      this.hexCoord = data.position;
      this.updateVisualPosition();
    }

    // Update effect animations (compare old vs new effects)
    this.updateEffectAnimations();

    // Redraw visuals
    this.drawVisuals();
  }

  /**
   * Set visibility based on fog of war.
   * Docked units stay hidden regardless of fog state.
   */
  public setVisibility(fullyVisible: boolean, explored: boolean): void {
    // Docked units are completely hidden — don't touch them
    if (this._isDocked) return;

    this.setVisible(true);
    if (!explored) {
      this.setAlpha(0);
    } else if (!fullyVisible) {
      this.setAlpha(0.3);
    } else {
      this.setAlpha(1);
    }
  }

  /**
   * Enter docked state — completely remove from map rendering.
   * Uses alpha=0, visible=false, scale=0, and disables interaction.
   */
  public enterDockedState(): void {
    this._isDocked = true;
    this.setVisible(false);
    this.setAlpha(0);
    this.setScale(0);
    this.setDepth(-10); // Below planets (depth 5) so even if visibility leaks, they're hidden
    this.setSelected(false);
    this.disableInteractive();
  }

  /**
   * Exit docked state — restore to normal map rendering.
   */
  public exitDockedState(): void {
    this._isDocked = false;
    this.setVisible(true);
    this.setScale(1);
    this.setAlpha(1);
    this.setDepth(10); // Restore above planets
    this.setInteractive(
      new Phaser.Geom.Rectangle(-30, -33, 60, 60),
      Phaser.Geom.Rectangle.Contains,
    );
  }

  /**
   * Draw all visual elements
   */
  public drawVisuals(): void {
    this.drawOwnerIndicator();
    this.drawSprite();
    this.drawHealthBar();
    this.drawShieldBar();
    this.drawMovementIndicator();
    this.drawEffectDots();
    this.drawNameLabel();
  }

  private drawOwnerIndicator(): void {
    this.ownerIndicator.clear();
    const isLeviathan = this.unitType.toLowerCase().includes('leviathan');
    const isBounty = this.ownerId.startsWith('AI_BOUNTY_');
    const isCarryingBounty = !isBounty && this.customName?.startsWith('BOUNTY|');

    if (isCarryingBounty) {
      // Player carrying bounty cargo — bright gold pulsing glow (visible to everyone)
      this.ownerIndicator.fillStyle(0xffdd00, 0.2);
      this.ownerIndicator.fillCircle(0, 0, 40);
      this.ownerIndicator.fillStyle(0xffaa00, 0.3);
      this.ownerIndicator.fillCircle(0, 0, 28);
      this.ownerIndicator.lineStyle(3, 0xffd700, 0.9);
      this.ownerIndicator.strokeCircle(0, 0, 40);
      if (!this.bountyPulseTween) {
        this.bountyPulseTween = this.scene.tweens.add({
          targets: this.ownerIndicator,
          alpha: { from: 1, to: 0.3 },
          duration: 600,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    } else if (isBounty) {
      // Bounty ship — pulsing gold/orange glow
      this.ownerIndicator.fillStyle(0xffaa00, 0.15);
      this.ownerIndicator.fillCircle(0, 0, 35);
      this.ownerIndicator.fillStyle(0xffdd00, 0.25);
      this.ownerIndicator.fillCircle(0, 0, 24);
      this.ownerIndicator.lineStyle(2, 0xffcc00, 0.8);
      this.ownerIndicator.strokeCircle(0, 0, 35);
      // Start pulse animation if not already running
      if (!this.bountyPulseTween) {
        this.bountyPulseTween = this.scene.tweens.add({
          targets: this.ownerIndicator,
          alpha: { from: 1, to: 0.4 },
          duration: 800,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    } else if (isLeviathan) {
      // Larger alien indicator — purple/teal glow
      this.ownerIndicator.fillStyle(0x8844cc, 0.2);
      this.ownerIndicator.fillCircle(0, 0, 90);
      this.ownerIndicator.lineStyle(2, 0x44ccaa, 0.6);
      this.ownerIndicator.strokeCircle(0, 0, 90);
    } else {
      const color = this.getOwnerColor();
      this.ownerIndicator.fillStyle(color, 0.10);
      this.ownerIndicator.fillCircle(0, 0, 22);
    }
  }

  private drawSprite(): void {
    this.sprite.clear();

    // Try to use ship sprite for scout units
    const spriteKey = this.getSpriteKey();
    if (spriteKey && this.scene.textures.exists(spriteKey)) {
      // Hide programmatic graphics
      this.sprite.setVisible(false);

      const isLeviathan = spriteKey === 'alien-leviathan';

      if (!this.shipSprite) {
        this.shipSprite = this.scene.add.image(0, -18, spriteKey);
        if (isLeviathan) {
          this.shipSprite.setDisplaySize(260, 180);
          this.setSize(260, 180);
          this.setInteractive(
            new Phaser.Geom.Rectangle(-130, -93, 260, 180),
            Phaser.Geom.Rectangle.Contains,
          );
        } else {
          this.shipSprite.setDisplaySize(76, 76);
        }
        this.baseScale = this.shipSprite.scaleX;
        this.add(this.shipSprite);
      } else if (this.shipSprite.texture.key !== spriteKey) {
        this.shipSprite.setTexture(spriteKey);
        this.shipSprite.setDisplaySize(76, 76);
        this.baseScale = this.shipSprite.scaleX;
      }
    } else {
      // Fallback: programmatic triangle for non-scout unit types
      if (this.shipSprite) {
        this.shipSprite.setVisible(false);
      }
      this.sprite.setVisible(true);

      const color = this.getOwnerColor();
      this.sprite.fillStyle(color, 1);
      this.sprite.beginPath();
      this.sprite.moveTo(0, -20);
      this.sprite.lineTo(15, 15);
      this.sprite.lineTo(-15, 15);
      this.sprite.closePath();
      this.sprite.fillPath();

      this.sprite.lineStyle(2, 0xffffff, 0.5);
      this.sprite.strokePath();

      this.sprite.fillStyle(0xffffff, 0.8);
      this.sprite.fillCircle(0, 0, 3);
    }
  }

  private getSpriteKey(): string | null {
    const type = this.unitType.toLowerCase();
    if (type.includes('leviathan') || type.includes('alien_leviathan')) return 'alien-leviathan';
    if (type.includes('dreadnaught') || type.includes('dreadnought')) return 'dreadnaught';
    if (type.includes('titan')) return 'titan';
    if (type.includes('carrier')) return 'carrier';
    if (type.includes('battleship')) return 'battleship';
    if (type.includes('destroyer')) return 'destroyer';
    if (type.includes('bomber')) return 'bomber';

    // Faction-specific sprites for pirate and EDF ships
    const faction = this.getFactionPrefix();

    if (type.includes('cruiser')) return faction ? `${faction}-cruiser` : 'cruiser';
    if (type.includes('fighter')) return faction ? `${faction}-fighter` : 'fighter';
    if (type.includes('scout'))  return faction ? `${faction}-scout` : 'scout';
    if (type.includes('tug')) return 'tug';
    if (type.includes('trade')) return 'tradeship';
    return null;
  }

  private getFactionPrefix(): string | null {
    if (this.ownerId.startsWith('AI_PIRATE_')) return 'pirate';
    if (this.ownerId.startsWith('AI_EDF_')) return 'edf';
    return null;
  }

  

  private get isDamaged(): boolean {
    return this.currentHp < this.maxHp || this.currentShields < this.maxShields;
  }

  private get shouldShowBars(): boolean {
    return this._isSelected || this.isDamaged;
  }

  private drawHealthBar(): void {
    this.healthBar.clear();
    if (!this.shouldShowBars) return;

    const radius = 24;
    const lineWidth = 2;
    const startAngle = -Math.PI / 2;

    this.healthBar.lineStyle(lineWidth, 0x333333, 0.25);
    this.healthBar.strokeCircle(0, 0, radius);

    const healthPercent = Math.max(0, Math.min(1, this.currentHp / this.maxHp));
    if (healthPercent > 0) {
      const healthColor = healthPercent > 0.5 ? 0x44ff44 : healthPercent > 0.25 ? 0xffff44 : 0xff4444;
      const endAngle = startAngle + Math.PI * 2 * healthPercent;
      this.healthBar.lineStyle(lineWidth, healthColor, 0.6);
      this.healthBar.beginPath();
      this.healthBar.arc(0, 0, radius, startAngle, endAngle, false);
      this.healthBar.strokePath();
    }
  }

  private drawShieldBar(): void {
    this.shieldBar.clear();
    if (!this.shouldShowBars) return;
    if (this.maxShields <= 0) return;

    const radius = 29;
    const lineWidth = 2;
    const startAngle = -Math.PI / 2;

    this.shieldBar.lineStyle(lineWidth, 0x333333, 0.2);
    this.shieldBar.strokeCircle(0, 0, radius);

    const shieldPercent = Math.max(0, Math.min(1, this.currentShields / this.maxShields));
    if (shieldPercent > 0) {
      const endAngle = startAngle + Math.PI * 2 * shieldPercent;
      this.shieldBar.lineStyle(lineWidth, 0x4488ff, 0.5);
      this.shieldBar.beginPath();
      this.shieldBar.arc(0, 0, radius, startAngle, endAngle, false);
      this.shieldBar.strokePath();
    }
  }

  private drawMovementIndicator(): void {
    // Clean up old indicator + text
    if (this.movementIndicator) {
      this.movementIndicator.destroy();
      this.movementIndicator = null;
    }
    if (this.movementText) {
      this.movementText.destroy();
      this.movementText = null;
    }

    // Show movement order if present
    if (this.movementOrder) {
      this.movementIndicator = this.scene.add.graphics();
      this.add(this.movementIndicator);

      // Draw arrow or path indicator
      this.movementIndicator.lineStyle(2, 0xffff00, 0.6);
      this.movementIndicator.fillStyle(0xffff00, 0.3);
      this.movementIndicator.fillCircle(20, -20, 5);

      // Show turns remaining
      this.movementText = this.scene.add.text(20, -20, `${this.movementOrder.turnsRemaining}`, {
        fontSize: '10px',
        color: '#ffff00',
      });
      this.movementText.setOrigin(0.5);
      this.add(this.movementText);
    }
  }

  /**
   * Draw status effect indicator dots below the unit
   */
  private drawEffectDots(): void {
    if (!this.effectDots) {
      this.effectDots = this.scene.add.graphics();
      this.add(this.effectDots);
    }
    this.effectDots.clear();

    if (this.activeEffects.length === 0) return;

    const effectColors: Record<string, number> = {
      SENSOR_BOOSTED: 0x00ffff,
      EVASIVE: 0x44ff44,
      SPEED_BOOSTED: 0xffff00,
      SHIELD_BOOSTED: 0x4488ff,
      MISSILE_BARRAGE: 0xff4444,
      BROADSIDE: 0xff8800,
    };

    const dotRadius = 3;
    const spacing = 10;
    const startX = -((this.activeEffects.length - 1) * spacing) / 2;
    const y = 22;

    this.activeEffects.forEach((effect, i) => {
      const color = effectColors[effect.effectType] || 0xffffff;
      this.effectDots!.fillStyle(color, 0.9);
      this.effectDots!.fillCircle(startX + i * spacing, y, dotRadius);
    });

    // Speed boost: draw trailing dots behind unit in opposite direction of movement
    if (this.activeEffects.some(e => e.effectType === 'SPEED_BOOSTED')) {
      const dx = this.lastMoveDirection.dx;
      const dy = this.lastMoveDirection.dy;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const nx = -dx / len;
      const ny = -dy / len;

      const trailAlphas = [0.5, 0.3, 0.1];
      for (let i = 0; i < 3; i++) {
        const offset = (i + 1) * 10;
        this.effectDots!.fillStyle(0xffff00, trailAlphas[i]);
        this.effectDots!.fillCircle(nx * offset, ny * offset, 2);
      }
    }
  }

  /** Get display name: variant name if available, otherwise unit type */
  public getDisplayName(): string {
    if (this.shipVariant) {
      return this.shipVariant.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }
    return this.unitType.replace(/_/g, ' ');
  }

  private drawNameLabel(): void {
    const isBountyShip = this.ownerId.startsWith('AI_BOUNTY_');
    const isCarryingBounty = !isBountyShip && this.customName?.startsWith('BOUNTY|');

    if ((isBountyShip || isCarryingBounty) && this.customName) {
      // Parse custom name:
      //   Bounty ship: "FIGHT (solana): 50,000|1776700000000"
      //   Carrier:     "BOUNTY|FIGHT (solana): 50,000"
      let rewardText: string;
      let expiryEpoch = 0;

      if (isCarryingBounty) {
        // "BOUNTY|FIGHT (solana): 50,000"
        rewardText = this.customName.substring(7); // strip "BOUNTY|"
      } else {
        // "FIGHT (solana): 50,000|epochMs"
        const parts = this.customName.split('|');
        rewardText = parts[0];
        expiryEpoch = parts[1] ? parseInt(parts[1]) : 0;
      }

      let timerText = '';
      if (expiryEpoch > 0) {
        const remaining = Math.max(0, expiryEpoch - Date.now());
        const mins = Math.floor(remaining / 60000);
        const secs = Math.floor((remaining % 60000) / 1000);
        timerText = remaining > 0 ? `\n⏱ ${mins}m ${secs}s remaining` : '\n⏱ EXPIRED';
      }

      const icon = isCarryingBounty ? '💰' : '🎯';
      const prefix = isCarryingBounty ? 'CARRYING: ' : '';
      const bountyLabel = `${icon} ${prefix}${rewardText}${timerText}`;
      const labelColor = isCarryingBounty ? '#FFEE44' : '#FFD700';
      const glowColor = isCarryingBounty ? '#ffee00' : '#ffaa00';

      if (!this.nameLabel) {
        this.nameLabel = this.scene.add.text(0, -55, bountyLabel, {
          fontSize: '13px',
          fontStyle: 'bold',
          fontFamily: '"Orbitron", "Share Tech Mono", monospace',
          color: labelColor,
          align: 'center',
          stroke: '#000000',
          strokeThickness: 3,
          shadow: { offsetX: 0, offsetY: 0, color: glowColor, blur: 8, fill: true },
          lineSpacing: 4,
        });
        this.nameLabel.setOrigin(0.5, 1);
        this.nameLabel.setAlpha(1);
        this.nameLabel.setVisible(true);
        this.add(this.nameLabel);

        // Update timer every second (bounty ships only)
        if (expiryEpoch > 0) {
          this.scene.time.addEvent({
            delay: 1000,
            loop: true,
            callback: () => { if (this.scene && this.nameLabel) this.drawNameLabel(); },
          });
        }
      } else {
        this.nameLabel.setText(bountyLabel);
        this.nameLabel.setVisible(true);
      }
    } else {
      // Normal ship — small, dim label
      const label = this.getDisplayName();
      if (!this.nameLabel) {
        this.nameLabel = this.scene.add.text(0, 30, label, {
          fontSize: '9px',
          fontFamily: '"Share Tech Mono", monospace',
          color: '#88bbcc',
          align: 'center',
        });
        this.nameLabel.setOrigin(0.5, 0);
        this.nameLabel.setAlpha(0.6);
        this.nameLabel.setVisible(false);
        this.add(this.nameLabel);
      } else {
        this.nameLabel.setText(label);
      }
    }
  }

  /**
   * Force-snap unit to an authoritative server position without tweening.
   * Used on fog re-entry (Option 2) and periodic position sync (Option 4).
   */
  public forceSnapPosition(pos: { q: number; r: number }): void {
    this.hexCoord = pos;
    if (this.moveTween) {
      this.moveTween.destroy();
      this.moveTween = null;
    }
    const pixel = axialToPixel(pos.q, pos.r);
    this.setPosition(pixel.x, pixel.y);
  }

  public isPositionAnimating(): boolean {
    return !!this.moveTween && this.moveTween.isPlaying();
  }

  /**
   * Update visual position on screen with smooth tween interpolation.
   * If a multi-hex path is provided, animates hop-by-hop through each hex.
   */
  public updateVisualPosition(): void {
    const pixel = axialToPixel(this.hexCoord.q, this.hexCoord.r);

    if (this.isFirstPosition) {
      this.isFirstPosition = false;
      this.setPosition(pixel.x, pixel.y);
      return;
    }

    this.lastMoveDirection = { dx: pixel.x - this.x, dy: pixel.y - this.y };

    // Check for drift: if sprite is more than ~1.5 hexes from target, snap immediately
    // This prevents cumulative drift from interrupted tweens
    const driftDist = Math.hypot(pixel.x - this.x, pixel.y - this.y);
    if (driftDist > 130) { // ~1.5 hex diameters
      if (this.moveTween) { this.moveTween.destroy(); this.moveTween = null; }
      this.setPosition(pixel.x, pixel.y);
      EventBus.emit('unit-movement-finished', { unitId: this.id });
      return;
    }

    if (this.moveTween) {
      this.moveTween.destroy();
      this.moveTween = null;
    }

    this.moveTween = this.scene.tweens.add({
      targets: this,
      x: pixel.x,
      y: pixel.y,
      duration: 950, // Align closer to 1000ms tick interval
      ease: 'Sine.easeInOut',
      onComplete: () => {
        // Snap to exact pixel position on completion to prevent sub-pixel drift
        this.setPosition(pixel.x, pixel.y);
        this.moveTween = null;
        EventBus.emit('unit-movement-finished', { unitId: this.id });
      },
    });
  }

  /**
   * Set selection state — shows a pulsing glow disc behind the ship + subtle scale pulse
   */
  public setSelected(selected: boolean): void {
    this._isSelected = selected;
    if (selected) {
      this.startSelectionGlow();
    } else {
      this.stopSelectionGlow();
    }
  }

  /**
   * Override the selection glow color (e.g. red for move mode).
   * Pass undefined to reset to the default green.
   */
  public setSelectionRingColor(color?: number): void {
    this.selectionGlowColor = color ?? 0x44ff44;
    if (this._isSelected && this.selectionGlow) {
      this.drawSelectionGlow();
    }
  }

  private startSelectionGlow(): void {
    if (!this.selectionGlow) {
      this.selectionGlow = this.scene.add.graphics();
      this.add(this.selectionGlow);
      this.sendToBack(this.selectionGlow);
    }
    this.drawSelectionGlow();
    this.selectionGlow.setAlpha(0.15);

    if (this.selectionGlowTween) this.selectionGlowTween.destroy();
    this.selectionGlowTween = this.scene.tweens.add({
      targets: this.selectionGlow,
      alpha: { from: 0.15, to: 0.4 },
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    if (this.selectionScaleTween) this.selectionScaleTween.destroy();
    const scaleTarget = this.shipSprite || this.sprite;
    if (scaleTarget) {
      const bs = this.baseScale;
      scaleTarget.setScale(bs);
      this.selectionScaleTween = this.scene.tweens.add({
        targets: scaleTarget,
        scaleX: bs * 1.5,
        scaleY: bs * 1.5,
        duration: 800,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  private stopSelectionGlow(): void {
    if (this.selectionGlowTween) {
      this.selectionGlowTween.destroy();
      this.selectionGlowTween = null;
    }
    if (this.selectionScaleTween) {
      this.selectionScaleTween.destroy();
      this.selectionScaleTween = null;
    }
    if (this.selectionGlow) {
      this.selectionGlow.clear();
      this.selectionGlow.setAlpha(0);
    }
    this.selectionGlowColor = 0x44ff44;
    const scaleTarget = this.shipSprite || this.sprite;
    if (scaleTarget) {
      scaleTarget.setScale(this.baseScale);
    }
    if (this.shipSprite) {
      this.shipSprite.clearTint();
    }
  }

  private drawSelectionGlow(): void {
    if (!this.selectionGlow) return;
    this.selectionGlow.clear();
    const color = this.selectionGlowColor;

    // Soft layered glow: larger dim disc + smaller bright disc
    this.selectionGlow.fillStyle(color, 0.3);
    this.selectionGlow.fillCircle(0, 0, 36);
    this.selectionGlow.fillStyle(color, 0.5);
    this.selectionGlow.fillCircle(0, 0, 26);
    this.selectionGlow.fillStyle(color, 0.2);
    this.selectionGlow.fillCircle(0, 0, 44);
  }

  /**
   * Get owner color from UUID hash
   */
  private getOwnerColor(): number {
    // Hash the owner ID to get a consistent color index
    let hash = 0;
    for (let i = 0; i < this.ownerId.length; i++) {
      hash = (hash << 5) - hash + this.ownerId.charCodeAt(i);
      hash = hash & hash;
    }
    const index = Math.abs(hash) % PLAYER_COLORS.length;
    return PLAYER_COLORS[index];
  }

  // Docked ships are now fully hidden via setVisible(false) in updateFromServer()

  /**
   * Set engagement state — shows/hides a pulsing red ring around the unit
   */
  public setEngaged(engaged: boolean): void {
    if (this._isEngaged === engaged) return;
    this._isEngaged = engaged;

    if (engaged) {
      if (!this.engagementRing) {
        this.engagementRing = this.scene.add.graphics();
        this.add(this.engagementRing);
        this.sendToBack(this.engagementRing);
      }
      this.engagementRing.clear();
      this.engagementRing.lineStyle(2, 0xff3333, 0.8);
      this.engagementRing.strokeCircle(0, 0, 22);
      this.engagementRing.setAlpha(0.8);

      if (this.engagementTween) {
        this.engagementTween.destroy();
      }
      this.engagementTween = this.scene.tweens.add({
        targets: this.engagementRing,
        alpha: { from: 0.3, to: 0.8 },
        duration: 600,
        yoyo: true,
        repeat: -1,
      });
    } else {
      if (this.engagementTween) {
        this.engagementTween.destroy();
        this.engagementTween = null;
      }
      if (this.engagementRing) {
        this.engagementRing.clear();
        this.engagementRing.setAlpha(0);
      }
    }
  }

  /**
   * Compare previous and current active effects, create/destroy animations accordingly
   */
  private updateEffectAnimations(): void {
    const currentTypes = new Set(this.activeEffects.map(e => e.effectType));

    // Effects that were added
    currentTypes.forEach(type => {
      if (!this.previousEffectTypes.has(type)) {
        this.startEffectAnimation(type);
      }
    });

    // Effects that were removed
    this.previousEffectTypes.forEach(type => {
      if (!currentTypes.has(type)) {
        this.stopEffectAnimation(type);
      }
    });

    this.previousEffectTypes = currentTypes;
  }

  private startEffectAnimation(effectType: string): void {
    const activeSprite = this.shipSprite || this.sprite;

    switch (effectType) {
      case 'EVASIVE':
        // Horizontal scaleX oscillation on the sprite
        if (!this.evasiveTween) {
          const bs = this.baseScale;
          this.evasiveTween = this.scene.tweens.add({
            targets: activeSprite,
            scaleX: { from: bs * 0.9, to: bs * 1.1 },
            duration: 100,
            yoyo: true,
            repeat: -1,
          });
        }
        break;

      case 'SHIELD_BOOSTED':
        // Pulsing cyan glow ring
        if (!this.shieldBoostRing) {
          this.shieldBoostRing = this.scene.add.graphics();
          this.add(this.shieldBoostRing);
          this.sendToBack(this.shieldBoostRing);
        }
        this.shieldBoostRing.clear();
        this.shieldBoostRing.lineStyle(2, 0x4488ff, 0.4);
        this.shieldBoostRing.strokeCircle(0, 0, 30);
        this.shieldBoostRing.setAlpha(0.4);

        if (!this.shieldBoostTween) {
          this.shieldBoostTween = this.scene.tweens.add({
            targets: this.shieldBoostRing,
            alpha: { from: 0.2, to: 0.6 },
            duration: 800,
            yoyo: true,
            repeat: -1,
          });
        }
        break;

      case 'SENSOR_BOOSTED': {
        // One-shot expanding ring, then leave a subtle dotted ring
        const ring = this.scene.add.graphics();
        this.add(ring);
        ring.lineStyle(1, 0x00ffff, 1);
        ring.strokeCircle(0, 0, 20);

        this.scene.tweens.addCounter({
          from: 20,
          to: 60,
          duration: 600,
          onUpdate: (tween) => {
            const val = tween.getValue() as number;
            const alpha = 1 - (val - 20) / 40;
            ring.clear();
            ring.lineStyle(1, 0x00ffff, alpha);
            ring.strokeCircle(0, 0, val);
          },
          onComplete: () => {
            ring.destroy();
          },
        });
        // Keep a subtle static ring
        if (!this.sensorBoostRing) {
          this.sensorBoostRing = this.scene.add.graphics();
          this.add(this.sensorBoostRing);
          this.sendToBack(this.sensorBoostRing);
        }
        this.sensorBoostRing.clear();
        // Draw dotted ring using small arcs
        for (let i = 0; i < 12; i++) {
          const angle = (Math.PI * 2 * i) / 12;
          const x = Math.cos(angle) * 26;
          const y = Math.sin(angle) * 26;
          this.sensorBoostRing.fillStyle(0x00ffff, 0.3);
          this.sensorBoostRing.fillCircle(x, y, 1.5);
        }
        break;
      }

      case 'SPEED_BOOSTED':
        // Trail dots are drawn statically in drawEffectDots — no tween needed
        break;
    }
  }

  private stopEffectAnimation(effectType: string): void {
    switch (effectType) {
      case 'EVASIVE':
        if (this.evasiveTween) {
          this.evasiveTween.destroy();
          this.evasiveTween = null;
        }
        const activeSprite = this.shipSprite || this.sprite;
        if (activeSprite) {
          activeSprite.setScale(this.baseScale);
        }
        break;

      case 'SHIELD_BOOSTED':
        if (this.shieldBoostTween) {
          this.shieldBoostTween.destroy();
          this.shieldBoostTween = null;
        }
        if (this.shieldBoostRing) {
          this.shieldBoostRing.destroy();
          this.shieldBoostRing = null;
        }
        break;

      case 'SENSOR_BOOSTED':
        if (this.sensorBoostRing) {
          this.sensorBoostRing.destroy();
          this.sensorBoostRing = null;
        }
        break;
    }
  }

  /**
   * Flash a cyan shield overlay when shields take damage
   */
  public flashShield(shieldBroken: boolean): void {
    const flash = this.scene.add.graphics();
    this.add(flash);

    const radius = shieldBroken ? 32 : 25;
    const alpha = shieldBroken ? 0.8 : 0.6;
    flash.fillStyle(0x4488ff, alpha);
    flash.fillCircle(0, 0, radius);

    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: shieldBroken ? 300 : 200,
      onComplete: () => {
        flash.destroy();
      },
    });
  }

  /**
   * Flash the unit when an ability is activated
   */
  public flashAbility(color: number): void {
    const activeSprite = this.shipSprite || this.sprite;

    // Flash white tint
    if (activeSprite instanceof Phaser.GameObjects.Image) {
      activeSprite.setTintFill(0xffffff);
      this.scene.time.delayedCall(150, () => {
        if (activeSprite && activeSprite.scene) {
          activeSprite.clearTint();
        }
      });
    }

    // Radial particle burst — 6 small dots expanding outward
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI * 2 * i) / 6;
      const particle = this.scene.add.graphics();
      this.add(particle);
      particle.fillStyle(color, 0.8);
      particle.fillCircle(0, 0, 3);

      const startX = Math.cos(angle) * 5;
      const startY = Math.sin(angle) * 5;
      const endX = Math.cos(angle) * 25;
      const endY = Math.sin(angle) * 25;

      particle.setPosition(startX, startY);

      this.scene.tweens.add({
        targets: particle,
        x: endX,
        y: endY,
        alpha: 0,
        duration: 300,
        onComplete: () => {
          particle.destroy();
        },
      });
    }
  }

  /**
   * Cleanup
   */
  public destroy(fromScene?: boolean): void {
    if (this.moveTween) {
      this.moveTween.destroy();
    }
    if (this.movementIndicator) {
      this.movementIndicator.destroy();
    }
    if (this.movementText) {
      this.movementText.destroy();
    }
    if (this.nameLabel) {
      this.nameLabel.destroy();
    }
    if (this.effectDots) {
      this.effectDots.destroy();
    }
    if (this.engagementTween) {
      this.engagementTween.destroy();
    }
    if (this.engagementRing) {
      this.engagementRing.destroy();
    }
    if (this.selectionGlowTween) {
      this.selectionGlowTween.destroy();
    }
    if (this.selectionScaleTween) {
      this.selectionScaleTween.destroy();
    }
    if (this.selectionGlow) {
      this.selectionGlow.destroy();
    }
    if (this.evasiveTween) {
      this.evasiveTween.destroy();
    }
    if (this.shieldBoostTween) {
      this.shieldBoostTween.destroy();
    }
    if (this.shieldBoostRing) {
      this.shieldBoostRing.destroy();
    }
    if (this.sensorBoostRing) {
      this.sensorBoostRing.destroy();
    }
    super.destroy(fromScene);
  }
}
